/**
 * Curated Unsplash placeholder images (Vedic-astrology-adjacent: mandalas,
 * star charts, planets, golden celestial motifs) used as the initial/
 * fallback image for each <DynamicImage> slot until an ADMIN uploads a
 * replacement via /admin → "Medien & Seitenbilder".
 *
 * `w=1200&q=80&auto=format` keeps these reasonably sized; Unsplash's
 * source/CDN URLs are stable, freely usable placeholder imagery (Unsplash
 * License), not to be treated as CMS-managed final production assets.
 *
 * `category` powers the filter tabs in the Admin media manager:
 * "offers" (package cards & booking modal), "pages" (per-page hero/section
 * images), "global" (shared across all pages, e.g. footer banner).
 */
export type PageImageCategory = "offers" | "pages" | "global";

export type PageImageSlot = {
  pagePath: string;
  sectionName: string;
  label: string;
  category: PageImageCategory;
  defaultImage: string;
  alt: string;
};

export const PAGE_IMAGE_SLOTS: PageImageSlot[] = [
  // ---- pages ---------------------------------------------------------
  {
    pagePath: "/",
    sectionName: "hero",
    label: "Startseite — Hero-Bild",
    category: "pages",
    defaultImage:
      "https://images.unsplash.com/photo-1504333638930-c8787321eee0?w=1200&q=80&auto=format&fit=crop",
    alt: "Goldener Sternenhimmel über einem Mandala-Muster",
  },
  {
    pagePath: "/",
    sectionName: "contact",
    label: "Startseite — Kontakt-Sektion",
    category: "pages",
    defaultImage:
      "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=1200&q=80&auto=format&fit=crop",
    alt: "Sternenhimmel bei Nacht als Symbol für kosmische Verbindung",
  },
  {
    pagePath: "/info",
    sectionName: "about",
    label: "Über uns — Atmosphäre-Bild",
    category: "pages",
    defaultImage:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&q=80&auto=format&fit=crop",
    alt: "Weites Sternenfeld als Sinnbild für vedische Weisheit",
  },
  {
    pagePath: "/info",
    sectionName: "profile_portrait",
    label: "Über uns — Portrait / Meister-Profil",
    category: "pages",
    defaultImage:
      "https://images.unsplash.com/photo-1600096194534-95cf5ece04cf?w=1200&q=80&auto=format&fit=crop",
    alt: "Porträt-Atmosphäre mit warmem, goldenem Licht",
  },

  // ---- offers & packages ----------------------------------------------
  {
    pagePath: "/angebote",
    sectionName: "hero",
    label: "Angebote — Hero-Bild",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1447433589675-4aaa569f3e05?w=1200&q=80&auto=format&fit=crop",
    alt: "Goldene Planetenkonstellation vor dunklem Hintergrund",
  },
  {
    pagePath: "/angebote",
    sectionName: "card_janma_kundali",
    label: "Paket-Karte — Janma Kundali (Geburtshoroskop & Seelenplan)",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=1200&q=80&auto=format&fit=crop",
    alt: "Sternenkarte als Symbol für das Geburtshoroskop",
  },
  {
    pagePath: "/angebote",
    sectionName: "card_dasha",
    label: "Paket-Karte — Dasha (Lebensphasen & Transite)",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1200&q=80&auto=format&fit=crop",
    alt: "Planetenbahnen als Sinnbild für Lebensphasen und Timing",
  },
  {
    pagePath: "/angebote",
    sectionName: "card_synastry",
    label: "Paket-Karte — Synastrie (Partnerschafts-Horoskop)",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&q=80&auto=format&fit=crop",
    alt: "Zwei sich verbindende Lichtkreise als Symbol für Partnerschaft",
  },
  {
    pagePath: "/angebote",
    sectionName: "card_vip",
    label: "Paket-Karte — VIP 1:1 Intensiv-Consultation",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=1200&q=80&auto=format&fit=crop",
    alt: "Goldener Kosmos als Symbol für exklusive Beratung",
  },
  {
    pagePath: "/angebote",
    sectionName: "modal_booking_header",
    label: "Buchungs-Modal — Header-Banner",
    category: "offers",
    defaultImage:
      "https://images.unsplash.com/photo-1543722530-d2c3201371e7?w=1200&q=80&auto=format&fit=crop",
    alt: "Kosmisches Banner über dem Buchungsformular",
  },

  // ---- global -----------------------------------------------------------
  {
    pagePath: "*",
    sectionName: "footer_banner",
    label: "Global — Kosmischer Akzentbereich über dem Footer",
    category: "global",
    defaultImage:
      "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1600&q=80&auto=format&fit=crop",
    alt: "Weiter Sternenhimmel als kosmischer Akzent vor dem Footer",
  },
];

/** Offer id (from lib/store.ts DEFAULT_OFFERS) → its card image section name. */
export const OFFER_ID_TO_IMAGE_SECTION: Record<string, string> = {
  "janma-kundali": "card_janma_kundali",
  "dasha-prognose": "card_dasha",
  synastrie: "card_synastry",
  "vip-coaching": "card_vip",
};

export function findImageSlot(pagePath: string, sectionName: string): PageImageSlot {
  const slot = PAGE_IMAGE_SLOTS.find((s) => s.pagePath === pagePath && s.sectionName === sectionName);
  if (!slot) {
    throw new Error(`No page-image slot registered for ${pagePath} / ${sectionName}`);
  }
  return slot;
}
